export const USER_ROLES = {
  ADMINISTRADOR: 1,
  GESTOR: 2,
  DOCENTE: 3,
  ALUNO: 4,
  SUPORTE: 5,
};

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

const { ADMINISTRADOR, GESTOR, DOCENTE, ALUNO, SUPORTE } = USER_ROLES;

//? Todo mundo menos o Aluno (que só enxerga a Agenda) e o Suporte (só Equipamentos).
const EQUIPE: UserRole[] = [ADMINISTRADOR, GESTOR, DOCENTE];

//? Rotas que dispensam autenticação. `/cursos/certificado` valida um certificado por UUID
//? (a rota da API também é pública) e é aberta por quem recebeu o link, logado ou não.
//? `/sala` é a sala conectada da lousa: o aluno chega pelo QR Code, sem conta no Portal.
export const ROTAS_PUBLICAS = ['/login', '/cursos/certificado', '/sala'];

/**
 * Quem pode abrir cada rota — espelha a visibilidade da Sidebar, que era a única barreira até
 * aqui (esconder o item do menu não impede digitar a URL). Consumido pelo guard global
 * `src/middleware/auth.global.ts`. O primeiro prefixo que casar decide, então os mais
 * específicos vêm primeiro. Rota sem regra (404 do Nuxt) apenas exige login.
 *
 * Mora aqui, e não em `utils/`, para o módulo continuar sem imports — é o que deixa
 * `scripts/check-rotas.mjs` carregá-lo direto no node, sem o resolver do Nuxt.
 */
const ROTAS: Array<[string, UserRole[]]> = [
  ['/usuarios/tipos', [ADMINISTRADOR]],
  ['/organizacao', [ADMINISTRADOR]],
  ['/perfis', [ADMINISTRADOR]],
  ['/bncc', [ADMINISTRADOR]],
  //? O Educador também entra: o guia dele mora aqui, e um guia que o destinatário
  //? não consegue abrir não serve para nada. A página filtra por tipo o que cada um vê.
  ['/guias', [ADMINISTRADOR, GESTOR, DOCENTE]],
  ['/agenda', [ADMINISTRADOR, GESTOR, DOCENTE, ALUNO]],
  //? Manutenção do acervo dos apps offline. Educador e aluno só leem — pelo app, não pelo Portal.
  ['/acervo', [ADMINISTRADOR, GESTOR]],
  ['/aplicativos', EQUIPE],
  ['/cursos', EQUIPE],
  //? Única rota do Suporte: ele atende os equipamentos de qualquer escola e nada mais.
  ['/equipamentos', [...EQUIPE, SUPORTE]],
  ['/escolas', EQUIPE],
  ['/planos', EQUIPE],
  ['/relatorios', EQUIPE],
  ['/turmas', EQUIPE],
  ['/usuarios', EQUIPE],
  ['/', EQUIPE],
];

const casa = (path: string, prefixo: string) => path === prefixo || path.startsWith(`${prefixo}/`);

export const rotaPublica = (path: string) => ROTAS_PUBLICAS.some(rota => casa(path, rota));

/** Página inicial de cada tipo — destino do redirect quando a rota é negada. */
export const rotaHome = (tipoId?: number | null) => {
  if (tipoId === ALUNO) return '/agenda';
  if (tipoId === SUPORTE) return '/equipamentos';
  return '/';
};

export const rotaPermitida = (path: string, tipoId?: number | null): boolean => {
  const regra = ROTAS.find(([prefixo]) => casa(path, prefixo));
  if (!regra) return true;
  return regra[1].includes(tipoId as UserRole);
};
